import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, provide, readonly } from "vue"
import {
  RejectApprovalMutation,
  RejectApprovalMutationMutation,
  RejectApprovalMutationMutationVariables,
  ClientMissionLifecycleFragmentFragment,
  MissionLifecycleFragmentFragment,
} from "@/gql"
import PricingEditor2 from "@/components/missions/PricingEditor2.vue"
import Previewable from "@/components/layout/Previewable.vue"
import { RefetchQueryDescription } from "apollo-client/core/watchQueryOptions"
import useRouter from "@/router/useRouter"
import CalloutCard from "@/modules/Missions/components/CalloutCard/CalloutCard.vue"
import AcceptApprovalFormModal from "@/components/forms/AcceptApprovalFormModal.vue"
import PaymentMilestonePrintable from "@/modules/Missions/components/PaymentMilestones/PaymentMilestonePrintable.vue"
import TeamMembersPrintable from "../Details/TeamMembers/TeamMembersPrintable.vue"
import { useMutation } from "@/hooks/useMutation"
import { useNotification } from "@/hooks/useNotification"
import { useProposalSteps } from "@/modules/Missions/hooks/useProposalSteps"
import Icon from "@/components/widgets/icons/Icon.vue"


export default /*#__PURE__*/_defineComponent({
  __name: 'ApproveMissionProposal',
  props: {
    mission: null,
    refetchQueries: null
  },
  setup(__props: any) {

const props = __props as {
  mission: ClientMissionLifecycleFragmentFragment | MissionLifecycleFragmentFragment
  refetchQueries: RefetchQueryDescription
}



const { router } = useRouter()
const { mutate } = useMutation()
const { addSuccess } = useNotification()

const { currentStep, isFinalStep, nextStep, resetSteps } = useProposalSteps()

const refetchQueries = computed(() => {
  return {
    mission: {
      query: "MissionLifecyclesQuery",
      variables: {
        filter: {
          idIn: [router.currentRoute.params.id],
        },
      },
    },
  }
})

provide("refetchQueries", readonly(refetchQueries.value))

const showProposalReviewModal = ref(false)
const showConfirmApprovalDialog = ref(false)
const isSubmitting = ref(false)
const showRejectModal = ref(false)
const allApproved = ref(true)

const missionScope = computed(() => props.mission?.missionScope)
const pricingProposal = computed(() => props.mission.missionProposal?.missionPricingProposal)
const missionProposal = computed(() => props.mission.missionProposal)

const title = computed(() => {
  const titles = ["Pricing proposal", "Payment milestones", "Team members"]
  return titles[currentStep.value - 1]
})

const reviewProposal = () => {
  showProposalReviewModal.value = true
}

const rejectionReasons = ref("")

const form = ref({
  rejectionReason: "",
})

const updateRejectionReason = (reason: string) => {
  if (!reason) return
  rejectionReasons.value = rejectionReasons.value ? `${rejectionReasons.value}, ${reason}` : reason
}

const onFinalStepApproval = () => {
  if (allApproved.value) {
    showConfirmApprovalDialog.value = true
  } else {
    onSubmitRejectionReason()
  }
}

const onClickApprove = () => {
  if (isFinalStep.value) {
    onFinalStepApproval()
  } else {
    nextStep()
  }
}

const onClickReject = () => {
  showRejectModal.value = true
}

const defaultStep = {
  1: false,
  2: false,
  3: false,
}

const rejectedSteps = ref<Record<number, boolean>>({
  ...defaultStep,
})

const onSubmitRejectionReason = async () => {
  allApproved.value = false
  updateRejectionReason(form.value.rejectionReason)
  rejectedSteps.value[currentStep.value] = true

  if (isFinalStep.value) {
    await handleRejectApproval()
  } else {
    onCancel()
    nextStep()
  }
}

const handleRejectApproval = async () => {
  if (!missionProposal.value?.clientApproval?.id) return
  isSubmitting.value = true
  const result = await mutate<
    RejectApprovalMutationMutation,
    RejectApprovalMutationMutationVariables
  >({
    mutation: RejectApprovalMutation,
    variables: {
      approvalId: missionProposal.value?.clientApproval?.prn,
      reason: rejectionReasons.value,
    },
    done: () => {
      isSubmitting.value = false
    },
    refetchQueries: props.refetchQueries,
  })
  if (result?.data?.rejectApproval && !result?.data?.rejectApproval.error) {
    addSuccess(`Mission Proposal rejected`)
    onCancel()
  }
}

const observer = ref(null)

const onCancel = () => {
  showRejectModal.value = false
  form.value.rejectionReason = ""
  observer.value && (observer.value as any).reset()
}

const stages = [
  { label: "Pricing proposal", step: 1 },
  { label: "Payment milestones", step: 2 },
  { label: "Team members", step: 3 },
]

const getStageData = (step: number) => {
  const isRejected = rejectedSteps.value[step]
  return {
    iconSet: isRejected ? "basil" : "solar",
    name: isRejected ? "cancel" : currentStep.value > step ? "check-circle" : "record",
    type: isRejected ? "solid" : currentStep.value > step ? "bold" : "outline",
    color: isRejected ? "red" : currentStep.value > step ? "green" : "grey",
  }
}

const onReset = () => {
  rejectedSteps.value = {
    ...defaultStep,
  }
  resetSteps()
}

const onPreviewClose = () => {
  showProposalReviewModal.value = false
  onReset()
}

return { __sfc: true,props, router, mutate, addSuccess, currentStep, isFinalStep, nextStep, resetSteps, refetchQueries, showProposalReviewModal, showConfirmApprovalDialog, isSubmitting, showRejectModal, allApproved, missionScope, pricingProposal, missionProposal, title, reviewProposal, rejectionReasons, form, updateRejectionReason, onFinalStepApproval, onClickApprove, onClickReject, defaultStep, rejectedSteps, onSubmitRejectionReason, handleRejectApproval, observer, onCancel, stages, getStageData, onReset, onPreviewClose, PricingEditor2, Previewable, CalloutCard, AcceptApprovalFormModal, PaymentMilestonePrintable, TeamMembersPrintable, Icon }
}

})